import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api, { withApiPrefix } from '../../services/api';
import styles from './ChapterReader.module.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import { 
  SettingsIcon, 
  GlobeIcon, 
  LanguageIcon, 
  DocumentIcon, 
  StarIcon, 
  HeartIcon,
  BookIcon, 
  LoaderIcon,
  CloseIcon,
  LearnIcon,
} from '../../components/Icons';
import WordModal from './components/WordModal/WordModal';
import SentencesSection from './components/SentencesSection/SentencesSection';
import NoteModal from './components/NoteModal/NoteModal';
import ChapterPlayer from './components/ChapterPlayer/ChapterPlayer';
import ChapterAINotes from './components/ChapterAINotes/ChapterAINotes';
import { parseSubtitleContent, buildSentenceTimingMap } from '../../utils/subtitle';
import useAudioUrls from '../../hooks/useAudioUrls';
const EN_AUDIO_KEYS = ['en_mp3', 'en_srt'];
const AUDIO_SEGMENT_END_TOLERANCE = 0.05;
const DEFAULT_SEGMENT_DURATION = 3;

const ensureAudioMetadata = (audio) => {
  if (!audio) return Promise.resolve();
  if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handleLoadedMetadata = () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      resolve();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    audio.load();
  });
};

const seekAudioTime = (audio, time) => {
  if (!audio) return Promise.resolve();
  const targetTime = Number.isFinite(time) ? Math.max(time, 0) : 0;

  if (Math.abs(audio.currentTime - targetTime) < 0.05) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handleSeeked = () => {
      audio.removeEventListener('seeked', handleSeeked);
      resolve();
    };

    audio.addEventListener('seeked', handleSeeked, { once: true });

    try {
      audio.currentTime = targetTime;
    } catch (error) {
      audio.removeEventListener('seeked', handleSeeked);
      resolve();
    }
  });
};

const ChapterReader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookid');
  const chapterId = searchParams.get('chapterid');
  // console.log('[ChapterReader] render, path:', location.pathname, 'search:', location.search);
  const { isDark } = useTheme();
  const { isVip } = useAuth();
  const [isDesktop, setIsDesktop] = useState(false);
  const [chapter, setChapter] = useState(null);
  const [book, setBook] = useState(null);
  const [previousChapter, setPreviousChapter] = useState(null);
  const [nextChapter, setNextChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(18);
  const [lineHeight, setLineHeight] = useState(1.8);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff'); // 背景颜色
  const [chineseTextColor, setChineseTextColor] = useState('#333333'); // 中文字体颜色
  const [englishTextColor, setEnglishTextColor] = useState('#666666'); // 英文字体颜色
  const [displayMode, setDisplayMode] = useState('zh-en'); // 显示模式: 'zh-en'(中文在前), 'en-zh'(英文在前), 'zh-only'(仅中文), 'en-only'(仅英文)
  const [showDisplayModeMenu, setShowDisplayModeMenu] = useState(false); // 是否显示显示模式菜单
  const [wordLookup, setWordLookup] = useState(null); // 单词查询结果
  const [showWordModal, setShowWordModal] = useState(false); // 是否显示单词弹窗
  const [lookupLoading, setLookupLoading] = useState(false); // 查词加载状态
  const [currentSentenceId, setCurrentSentenceId] = useState(null); // 当前句子ID
  const [favoriteLoading, setFavoriteLoading] = useState(false); // 收藏加载状态
  const [isFavorited, setIsFavorited] = useState(false); // 是否已收藏
  const [activeDict, setActiveDict] = useState('youdao'); // 当前激活的字典
  const [dictResults, setDictResults] = useState({}); // 存储各字典的查询结果
  const [dictLoading, setDictLoading] = useState({}); // 各字典的加载状态
  const [wordModalHistory, setWordModalHistory] = useState([]); // 记录之前的单词弹窗历史
  const [sentenceFavorites, setSentenceFavorites] = useState({}); // 句子收藏状态 {sentenceId: boolean}
  const [sentenceFavoriteLoading, setSentenceFavoriteLoading] = useState({}); // 句子收藏加载状态
  const [aiNoteSaved, setAiNoteSaved] = useState({});
  const [aiNoteSavedSet, setAiNoteSavedSet] = useState(() => new Set());
  const [aiNoteSaving, setAiNoteSaving] = useState({});
  const [showSettings, setShowSettings] = useState(false); // 是否显示设置面板
  const settingsRef = useRef(null); // 设置容器的引用
  const displayModeMenuRef = useRef(null); // 显示模式菜单引用
  const [highlightSentenceId, setHighlightSentenceId] = useState(null); // 要高亮的句子ID
  const [activeTab, setActiveTab] = useState('article');
  const [chapterWords, setChapterWords] = useState([]);
  const [chapterWordSet, setChapterWordSet] = useState(new Set());
  const [wordsLoading, setWordsLoading] = useState(false);
  const [wordFavoriteLoading, setWordFavoriteLoading] = useState({});
  const [expandedSentences, setExpandedSentences] = useState({});
  const [showAllWordDefinitions, setShowAllWordDefinitions] = useState(false);
  const [showAllWordSentences, setShowAllWordSentences] = useState(false);
  const [showChapterNavigation, setShowChapterNavigation] = useState(true); // 是否显示章节导航
  const [listeningControlsContainer, setListeningControlsContainer] = useState(null); // 听力底部控制容器
  const [pdfExportLoading, setPdfExportLoading] = useState(false); // PDF导出加载状态
  const [showPdfVipTip, setShowPdfVipTip] = useState(false); // 非VIP点击PDF时显示提示
  const [showPdfQuotaModal, setShowPdfQuotaModal] = useState(false); // 额度用尽提示窗
  const [pdfQuotaModalMessage, setPdfQuotaModalMessage] = useState('');
  const [sentenceNoteLoading, setSentenceNoteLoading] = useState({}); // 句子笔记加载状态
  const [noteModalState, setNoteModalState] = useState({
    visible: false,
    sentenceId: null,
    originalText: '',
    translatedText: ''
  });
  const [noteContent, setNoteContent] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [sentenceTimings, setSentenceTimings] = useState({ en: {}, cn: {} });
  const sentenceAudioRefs = useRef({ en: null, cn: null });
  const sentenceAudioSourcesRef = useRef({ en: '', cn: '' });
  const sentenceAudioPlaybackRef = useRef({
    cancelCurrent: null,
    isCancelled: false,
    sentenceId: null,
  });
  const [completionStatus, setCompletionStatus] = useState({ completed: false, completedAt: null });
  const [completionLoading, setCompletionLoading] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [highlightStyle, setHighlightStyle] = useState({ left: '4px', width: '0px' });
  const tabButtonRefs = useRef({});
  const tabItems = useMemo(
    () => [
      { key: 'article', label: '文章', disabled: false, title: '查看文章内容' },
      { key: 'listening', label: '听力', disabled: false, title: '查看听力练习' },
      {
        key: 'notes',
        label: 'AI笔记',
        disabled: false,
        title: '查看 AI 笔记',
      },
    ],
    []
  );
  const tabOrder = useMemo(() => tabItems.map((item) => item.key), [tabItems]);
  const completionDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    []
  );
  const [audioNeeded, setAudioNeeded] = useState(false);
  const [enAudioReady, setEnAudioReady] = useState(false);
  const { urls: enAudioUrls } = useAudioUrls(enAudioReady ? chapter?.id : null, EN_AUDIO_KEYS);
  const { urls: fullAudioUrls } = useAudioUrls(audioNeeded ? chapter?.id : null);
  const audioUrls = fullAudioUrls ? { ...enAudioUrls, ...fullAudioUrls } : enAudioUrls;
  const backPathRef = useRef(location.state?.from || null);
  const historyReturnRef = useRef(location.state?.historyReturn || null);
  const listeningControlsRef = useCallback((node) => {
    setListeningControlsContainer(node);
  }, []);
  const computedActiveTabIndex = useMemo(() => {
    const index = tabOrder.indexOf(activeTab);
    return index >= 0 ? index : 0;
  }, [activeTab, tabOrder]);

  const stopSentenceAudioPlayback = useCallback(() => {
    const playback = sentenceAudioPlaybackRef.current;
    playback.isCancelled = true;
    if (typeof playback.cancelCurrent === 'function') {
      playback.cancelCurrent();
    }
    playback.cancelCurrent = null;
    playback.sentenceId = null;
  }, []);

  const ensureSentenceAudio = useCallback(
    (language) => {
      if (!chapter?.id || !audioUrls) return null;
      if (language !== 'en' && language !== 'cn') return null;

      const urlKey = language === 'en' ? 'en_mp3' : 'cn_mp3';
      const targetSrc = audioUrls[urlKey];
      if (!targetSrc) return null;

      const audioMap = sentenceAudioRefs.current;
      const sourceMap = sentenceAudioSourcesRef.current;

      let audio = audioMap[language];
      if (!audio) {
        audio = new Audio(targetSrc);
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto';
        audioMap[language] = audio;
        sourceMap[language] = targetSrc;
      } else if (sourceMap[language] !== targetSrc) {
        audio.pause();
        audio.src = targetSrc;
        audio.load();
        sourceMap[language] = targetSrc;
      }

      return audio;
    },
    [chapter?.id, audioUrls]
  );

  const playSentenceAudioSegment = useCallback(
    async (audio, start = 0, end = null) => {
      if (!audio) return;
      const playback = sentenceAudioPlaybackRef.current;

      await ensureAudioMetadata(audio);
      if (playback.isCancelled) return;

      await seekAudioTime(audio, start ?? 0);
      if (playback.isCancelled) return;

      await new Promise((resolve) => {
        let finished = false;

        const finish = () => {
          if (finished) return;
          finished = true;
          audio.pause();
          audio.removeEventListener('timeupdate', handleTimeUpdate);
          audio.removeEventListener('ended', handleEnded);
          if (playback.cancelCurrent === finish) {
            playback.cancelCurrent = null;
          }
          resolve();
        };

        const handleTimeUpdate = () => {
          if (playback.isCancelled) {
            finish();
            return;
          }
          if (end != null && audio.currentTime >= end - AUDIO_SEGMENT_END_TOLERANCE) {
            finish();
          }
        };

        const handleEnded = () => {
          finish();
        };

        playback.cancelCurrent = () => {
          playback.isCancelled = true;
          finish();
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.playbackRate = 1;

        const playPromise = audio.play();
        if (playPromise?.catch) {
          playPromise.catch(() => finish());
        }
      });
    },
    []
  );

  const handlePlaySentenceAudio = useCallback(
    async (sentenceId, payload = {}) => {
      const playback = sentenceAudioPlaybackRef.current;

      if (playback.sentenceId === sentenceId && !playback.isCancelled) {
        stopSentenceAudioPlayback();
        return;
      }

      const timings = payload?.timings ?? {};
      const segments = [];

      if (timings.en?.start != null) {
        segments.push({ language: 'en', start: timings.en.start, end: timings.en.end });
      }
      if (timings.cn?.start != null) {
        segments.push({ language: 'cn', start: timings.cn.start, end: timings.cn.end });
      }

      if (!segments.length) {
        console.warn('未找到可用的句子音频时间戳', sentenceId, timings);
        return;
      }

      stopSentenceAudioPlayback();
      playback.isCancelled = false;
      playback.sentenceId = sentenceId;

      for (const segment of segments) {
        if (playback.isCancelled) break;
        const audio = ensureSentenceAudio(segment.language);
        if (!audio) continue;

        const startValue = Number.isFinite(segment.start) ? Math.max(segment.start, 0) : 0;
        const endValue = Number.isFinite(segment.end)
          ? Math.max(segment.end, startValue)
          : startValue + DEFAULT_SEGMENT_DURATION;

        try {
          await playSentenceAudioSegment(audio, startValue, endValue);
        } catch (error) {
          console.error('播放句子音频片段失败:', error);
          playback.isCancelled = true;
        }
      }

      if (playback.sentenceId === sentenceId) {
        playback.sentenceId = null;
        playback.cancelCurrent = null;
        playback.isCancelled = false;
      }
    },
    [ensureSentenceAudio, playSentenceAudioSegment, stopSentenceAudioPlayback]
  );

  useEffect(() => {
    const fetchCompletionStatus = async () => {
      try {
        setCompletionLoading(true);
        const { data } = await api.get(`/completed-chapters/status/${chapterId}`);
        if (data?.completed) {
          setCompletionStatus({
            completed: true,
            completedAt: data.completed_at ? new Date(data.completed_at) : null,
          });
        } else {
          setCompletionStatus({ completed: false, completedAt: null });
        }
        setCompletionError('');
      } catch (error) {
        console.error('获取章节完成状态失败:', error);
        setCompletionStatus({ completed: false, completedAt: null });
        setCompletionError('获取完成状态失败');
      } finally {
        setCompletionLoading(false);
      }
    };

    if (chapterId) {
      const timer = setTimeout(fetchCompletionStatus, 500);
      return () => clearTimeout(timer);
    } else {
      setCompletionStatus({ completed: false, completedAt: null });
    }
  }, [chapterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchSegments = async (srtUrl) => {
      if (!srtUrl) return [];
      try {
        const response = await fetch(srtUrl, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error(`加载字幕失败: ${response.status}`);
        }
        const text = await response.text();
        if (cancelled) {
          return [];
        }
        return parseSubtitleContent(text);
      } catch (error) {
        console.error('获取字幕失败:', error);
        return [];
      }
    };

    const loadTimings = async () => {
      if (!chapterId || !audioUrls) {
        setSentenceTimings({ en: {}, cn: {} });
        return;
      }

      const [enSegments, cnSegments] = await Promise.all([
        fetchSegments(audioUrls.en_srt),
        fetchSegments(audioUrls.cn_srt),
      ]);

      if (cancelled) {
        return;
      }

      const englishMap = buildSentenceTimingMap(enSegments);
      const chineseMap = buildSentenceTimingMap(cnSegments);
      setSentenceTimings({ en: englishMap, cn: chineseMap });
    };

    loadTimings();

    return () => {
      cancelled = true;
    };
  }, [chapterId, audioUrls]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(media.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  // 计算 tabHighlight 的位置和宽度
  const updateHighlight = useCallback(() => {
    const activeButton = tabButtonRefs.current[activeTab];
    if (activeButton) {
      const buttonLeft = activeButton.offsetLeft;
      const buttonWidth = activeButton.offsetWidth;
      // 缩短高亮条：宽度减少 8px (左右各 4px)
      const shrinkAmount = 8;
      const width = buttonWidth - shrinkAmount;
      const left = buttonLeft + shrinkAmount / 2;
      setHighlightStyle({ left: `${left}px`, width: `${width}px` });
    }
  }, [activeTab]);

  // 当 activeTab 变化时更新高亮位置
  useEffect(() => {
    updateHighlight();
    // 延迟执行确保 DOM 已渲染
    const timer = setTimeout(updateHighlight, 50);
    return () => clearTimeout(timer);
  }, [activeTab, updateHighlight]);

  // 组件挂载后初始化高亮位置
  useLayoutEffect(() => {
    // 使用 requestAnimationFrame 确保在下一帧渲染前执行
    const rafId = requestAnimationFrame(() => {
      updateHighlight();
    });
    
    // 双保险：延迟再执行一次
    const timer = setTimeout(() => {
      updateHighlight();
    }, 50);
    
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [updateHighlight]);

  useEffect(() => {
    stopSentenceAudioPlayback();
    Object.values(sentenceAudioRefs.current).forEach((audio) => {
      if (audio) {
        audio.pause();
      }
    });
    sentenceAudioSourcesRef.current.en = '';
    sentenceAudioSourcesRef.current.cn = '';

    return () => {
      stopSentenceAudioPlayback();
      Object.values(sentenceAudioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
        }
      });
    };
  }, [chapterId, stopSentenceAudioPlayback]);

  useEffect(() => {
    if (location.state?.from) {
      let fromPath = location.state.from;
      // 转换旧格式 /book/:id 为新格式 /book?bookid=:id
      const oldBookRouteMatch = fromPath.match(/^\/book\/(\d+)$/);
      if (oldBookRouteMatch) {
        fromPath = `/book?bookid=${oldBookRouteMatch[1]}`;
      }
      backPathRef.current = fromPath;
    }
    if (location.state?.historyReturn) {
      historyReturnRef.current = location.state.historyReturn;
    }
  }, [location.state]);

  useEffect(() => {
    if (!backPathRef.current && bookId) {
      backPathRef.current = `/book?bookid=${bookId}`;
    }
  }, [bookId]);

  // 处理URL参数中的句子高亮
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam) {
      const sentenceId = parseInt(highlightParam, 10);
      if (!isNaN(sentenceId)) {
        setHighlightSentenceId(sentenceId);
        // 延迟滚动到高亮句子，确保内容已加载
        setTimeout(() => {
          const element = document.getElementById(`sentence-${sentenceId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 1000);
      }
    }
  }, [searchParams]);

  // 监听主题变化，自动调整阅读设置
  useEffect(() => {
    if (isDark) {
      // 夜间模式：切换到夜间黑主题
      setBackgroundColor('#1a1a1a');
      setChineseTextColor('#e0e0e0');
      setEnglishTextColor('#b0b0b0');
    } else {
      // 白天模式：切换到默认白主题
      setBackgroundColor('#ffffff');
      setChineseTextColor('#333333');
      setEnglishTextColor('#666666');
    }
  }, [isDark]);


  // 点击外部关闭设置面板
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettings && settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings]);

  useEffect(() => {
    if (!showDisplayModeMenu) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (displayModeMenuRef.current && !displayModeMenuRef.current.contains(event.target)) {
        setShowDisplayModeMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowDisplayModeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDisplayModeMenu]);

  const fetchChapterContent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/chapters/${chapterId}`, {
        params: { source: 'reader' },
      });
      setChapter(response.data);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.code === 'DAILY_LIMIT_REACHED') {
        setError('今日阅读章节已达上限，升级 VIP 享受无限阅读');
      } else {
        console.error('获取章节内容失败:', error);
        setError('获取章节内容失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBookInfo = async () => {
    try {
      const response = await api.get(`/novels/${bookId}`);
      setBook(response.data);
    } catch (error) {
      console.error('获取书籍信息失败:', error);
    }
  };

  const fetchAdjacentChapters = async () => {
    if (!chapterId) return;
    
    try {
      // 并行获取上一章和下一章
      const [prevResponse, nextResponse] = await Promise.allSettled([
        api.get(`/chapters/${chapterId}/previous`),
        api.get(`/chapters/${chapterId}/next`)
      ]);
      
      // 处理上一章
      if (prevResponse.status === 'fulfilled') {
        setPreviousChapter(prevResponse.value.data);
        console.log('[ChapterNav] 上一章:', prevResponse.value.data);
      } else {
        setPreviousChapter(null);
        console.log('[ChapterNav] 没有上一章');
      }
      
      // 处理下一章
      if (nextResponse.status === 'fulfilled') {
        setNextChapter(nextResponse.value.data);
        console.log('[ChapterNav] 下一章:', nextResponse.value.data);
      } else {
        setNextChapter(null);
        console.log('[ChapterNav] 没有下一章');
      }
    } catch (error) {
      console.error('获取相邻章节失败:', error);
      setPreviousChapter(null);
      setNextChapter(null);
    }
  };

  const handleBackToBook = () => {
    console.log('[ChapterReader] handleBackToBook called');
    console.log('[ChapterReader] current path:', window.location.href);
    console.log('[ChapterReader] history.length:', window.history.length);
    console.log('[ChapterReader] historyReturnRef:', historyReturnRef.current);
    console.log('[ChapterReader] backPathRef:', backPathRef.current);
    if (historyReturnRef.current) {
      const fallback = bookId ? `/book?bookid=${bookId}` : '/discover';
      const target = backPathRef.current || fallback;
      const statePayload = {
        historyReturn: historyReturnRef.current,
        from: historyReturnRef.current?.from || historyReturnRef.current || target,
      };
      console.log('[ChapterReader] historyReturn mode, navigating to:', target);
      navigate(target, {
        replace: true,
        state: statePayload,
      });
      return;
    }
    console.log('[ChapterReader] calling navigate(-1)');
    navigate(-1);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const adjustFontSize = (delta) => {
    const newSize = Math.max(12, Math.min(24, fontSize + delta));
    setFontSize(newSize);
  };

  const adjustLineHeight = (delta) => {
    const newHeight = Math.max(1.2, Math.min(2.5, lineHeight + delta));
    setLineHeight(newHeight);
  };

  // 预设颜色主题
  const colorThemes = [
    { name: '默认白', bg: '#ffffff', chinese: '#333333', english: '#666666' },
    { name: '护眼绿', bg: '#f0f8e8', chinese: '#2d4a2b', english: '#4a6741' },
    { name: '暖米色', bg: '#faf6f0', chinese: '#5d4e37', english: '#8b6f47' },
    { name: '夜间黑', bg: '#1a1a1a', chinese: '#e0e0e0', english: '#b0b0b0' },
    { name: '深蓝夜', bg: '#0f1419', chinese: '#c9d1d9', english: '#8b949e' },
    { name: '羊皮纸', bg: '#f4f1e8', chinese: '#4a4a4a', english: '#6a6a6a' }
  ];

  // 应用颜色主题
  const applyColorTheme = (theme) => {
    setBackgroundColor(theme.bg);
    setChineseTextColor(theme.chinese);
    setEnglishTextColor(theme.english);
  };

  const displayModeOptions = useMemo(() => ([
    {
      value: 'zh-en',
      label: '中英',
      description: '中文句子在前，英文句子在后显示',
      icon: <GlobeIcon size={16} />
    },
    {
      value: 'en-zh',
      label: '英中',
      description: '英文句子在前，中文句子在后显示',
      icon: <GlobeIcon size={16} />
    },
    {
      value: 'zh-only',
      label: '中文',
      description: '仅显示中文原文句子',
      icon: <DocumentIcon size={16} />
    },
    {
      value: 'en-only',
      label: '英文',
      description: '仅显示英文翻译句子',
      icon: <LanguageIcon size={16} />
    }
  ]), []);

  const activeDisplayMode = useMemo(() => {
    return displayModeOptions.find(option => option.value === displayMode) || displayModeOptions[0];
  }, [displayMode, displayModeOptions]);

  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
    setShowDisplayModeMenu(false);
  };

  const handleWordFavoriteToggle = async (wordItem) => {
    if (!wordItem?.id) {
      return;
    }

    const wordId = wordItem.id;
    setWordFavoriteLoading(prev => ({ ...prev, [wordId]: true }));

    try {
      await api.delete(`/favorite-words/${wordId}`);
    } catch (error) {
      console.error('取消收藏单词失败:', error);
      if (error.response?.status === 401) {
        alert('请先登录后再操作');
      } else {
        alert('取消收藏失败，请稍后重试');
      }
    } finally {
      setWordFavoriteLoading(prev => {
        const next = { ...prev };
        delete next[wordId];
        return next;
      });
    }
  };

  const handleWordPronunciation = (word, type) => {
    const cleanWord = word?.trim();
    if (!cleanWord) {
      return;
    }

    try {
      const audio = new Audio(`http://dict.youdao.com/dictvoice?type=${type}&audio=${encodeURIComponent(cleanWord)}`);
      audio.play();
    } catch (error) {
      console.error('播放单词发音失败:', error);
    }
  };

  // 处理全局单词释义开关
  const handleToggleAllWordDefinitions = () => {
    setShowAllWordDefinitions(!showAllWordDefinitions);
  };

  // 处理全局单词句子开关
  const handleToggleAllWordSentences = async () => {
    const newValue = !showAllWordSentences;
    setShowAllWordSentences(newValue);
    
    // 如果开启全局句子显示，需要获取所有单词的句子详情
    if (newValue && chapterWords.length > 0) {
      // 延迟执行，确保状态更新完成
      setTimeout(async () => {
        for (const wordItem of chapterWords) {
          const wordId = wordItem.id;
          if (!expandedSentences[wordId]) {
            await handleShowSentenceDetail(wordItem);
          }
        }
      }, 50);
    }
  };


  // 导出PDF（仅 VIP）
const handleExportPDF = async () => {
  if (!isVip) {
    setShowPdfVipTip(true);
    return;
  }
  try {
    setPdfExportLoading(true);
      
      const response = await api.get(`/export-pdf/${chapterId}`, {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 获取文件名
      const contentDisposition = response.headers['content-disposition'];
      let filename = `chapter_${chapterId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF导出失败:', error);
      // 429/403 时后端返回 JSON，但本次请求是 responseType: 'blob'，需从 Blob 解析
      let detail = error?.response?.data?.detail;
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          detail = json?.detail ?? json;
        } catch (_) {}
      }
      const status = error?.response?.status;
      if (status === 429 || detail?.code === 'PDF_QUOTA_EXHAUSTED') {
        setPdfQuotaModalMessage(detail?.message || 'PDF 下载次数已用完，续费会员可获得更多下载次数。');
        setShowPdfQuotaModal(true);
      } else if (status === 403 || detail?.code === 'VIP_REQUIRED') {
        setPdfQuotaModalMessage(detail?.message || 'PDF 导出为 VIP 专属功能');
        setShowPdfQuotaModal(true);
      } else {
        setPdfQuotaModalMessage('PDF 导出失败，请稍后重试');
        setShowPdfQuotaModal(true);
      }
    } finally {
      setPdfExportLoading(false);
  }
};

  const handleToggleCompletion = async () => {
    if (!chapterId) return;
    setCompletionLoading(true);
    setCompletionError('');
    try {
      if (completionStatus.completed) {
        await api.delete(`/completed-chapters/${chapterId}`);
        setCompletionStatus({ completed: false, completedAt: null });
      } else {
        const { data } = await api.post(`/completed-chapters/${chapterId}`);
        setCompletionStatus({
          completed: true,
          completedAt: data?.completed_at ? new Date(data.completed_at) : new Date(),
        });
      }
    } catch (error) {
      console.error('更新完成状态失败:', error);
      setCompletionError('更新完成状态失败，请稍后重试');
    } finally {
      setCompletionLoading(false);
    }
  };

  // 处理显示句子详情
  const handleShowSentenceDetail = async (wordItem) => {
    const wordId = wordItem.id;
    
    // 如果已经展开，则收起
    if (expandedSentences[wordId]) {
      setExpandedSentences(prev => ({
        ...prev,
        [wordId]: null
      }));
      return;
    }

    try {
      // 获取章节内容
      const response = await api.get(`/chapters/${wordItem.chapter_id}`);
      const chapter = response.data;
      
      // 解析章节内容
      let contentSentences = [];
      let translationSentences = [];

      // 解析原文内容
      try {
        const contentData = JSON.parse(chapter.content);
        if (Array.isArray(contentData)) {
          contentSentences = contentData
            .map(item => {
              if (typeof item === 'string') return item;
              if (item && typeof item === 'object') {
                return (
                  item.sentence_content ||
                  item.content ||
                  ''
                );
              }
              return '';
            })
            .filter(text => text.trim());
        } else if (contentData?.sentences && Array.isArray(contentData.sentences)) {
          contentSentences = contentData.sentences
            .map(item =>
              item?.sentence_content ||
              item?.content ||
              ''
            )
            .filter(text => text.trim());
        } else {
          const text =
            contentData?.sentence_content ||
            contentData?.content ||
            '';
          if (text) contentSentences = [text];
        }
      } catch (e) {
        contentSentences = chapter.content.split(/\n+/).filter(p => p.trim());
      }

      // 解析翻译内容
      if (chapter.content_trans) {
        try {
          const transData = JSON.parse(chapter.content_trans);
          if (Array.isArray(transData)) {
            translationSentences = transData
              .map(item => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object') {
                  return (
                    item.sentence_content ||
                    item.content ||
                    ''
                  );
                }
                return '';
              })
              .filter(text => text.trim());
          } else if (transData?.sentences && Array.isArray(transData.sentences)) {
            translationSentences = transData.sentences
              .map(item =>
                item?.sentence_content ||
                item?.content ||
                ''
              )
              .filter(text => text.trim());
          } else {
            const text =
              transData?.sentence_content ||
              transData?.content ||
              '';
            if (text) translationSentences = [text];
          }
        } catch (e) {
          translationSentences = chapter.content_trans
            .split(/\n+/)
            .filter(p => p.trim());
        }
      }

      const sentenceIndexRaw = Number(wordItem.sentence_id ?? 0);
      const sentenceIndex = Number.isFinite(sentenceIndexRaw) && sentenceIndexRaw > 0
        ? sentenceIndexRaw - 1
        : Math.max(0, sentenceIndexRaw);
      const originalText = contentSentences[sentenceIndex] || '';
      const translatedText = translationSentences[sentenceIndex] || '';

      // 获取章节标题
      let chapterTitle = chapter.title;
      try {
        const contentData = JSON.parse(chapter.content);
        chapterTitle = contentData.chapter_title || chapter.title;
      } catch (e) {
        // 忽略JSON解析错误
      }

      setExpandedSentences(prev => ({
        ...prev,
        [wordId]: {
          chapterTitle,
          originalText,
          translatedText
        }
      }));
    } catch (error) {
      console.error('获取句子详情失败:', error);
      alert('获取句子详情失败，请稍后重试');
    }
  };

  // 跳转到章节并高亮句子
  const handleGoToChapterWithHighlight = (targetChapterId, targetSentenceId) => {
    if (targetChapterId === parseInt(chapterId)) {
      // 如果是当前章节，激活文章标签并高亮句子
      setActiveTab('article');
      setHighlightSentenceId(targetSentenceId);
      setTimeout(() => {
        const element = document.getElementById(`sentence-${targetSentenceId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      // 如果是其他章节，跳转并高亮
      navigate(`/read?bookid=${bookId}&chapterid=${targetChapterId}&highlight=${targetSentenceId}`, { replace: true });
    }
  };

  // 处理单词点击事件
  const handleWordClick = async (word, sentenceId) => {
    // 清理单词（移除标点符号）
    const cleanWord = word.replace(/[^\w\s]/gi, '').trim().toLowerCase();
    
    if (!cleanWord || cleanWord.length < 2) {
      return;
    }

    let normalizedSentenceId = null;
    if (typeof sentenceId === 'number' && Number.isFinite(sentenceId)) {
      normalizedSentenceId = sentenceId;
    } else if (typeof sentenceId === 'string') {
      const parsed = Number(sentenceId);
      if (Number.isFinite(parsed)) {
        normalizedSentenceId = parsed;
      }
    }

    console.log('查询单词:', cleanWord, '句子ID:', normalizedSentenceId ?? '无');

    if (showWordModal && wordLookup) {
      const historyEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        wordLookup,
        dictResults,
        dictLoading,
        isFavorited,
        favoriteLoading,
        activeDict,
        sentenceId: currentSentenceId,
        lookupLoading,
      };
      setWordModalHistory((prev) => [...prev, historyEntry]);
    }
    
    // 重置状态
    setShowWordModal(true);
    setWordLookup({ word: cleanWord });
    setCurrentSentenceId(normalizedSentenceId);
    setIsFavorited(false);
    setFavoriteLoading(false);
    setActiveDict('youdao');
    setDictResults({});
    setDictLoading({ youdao: true, collins: false }); // 先只查有道，Collins 等用户点击再查
    setLookupLoading(true);

    try {
      // 并行执行EDICT查词和收藏状态（Collins 延迟到用户点击）
      const favoriteCheckPromise =
        normalizedSentenceId !== null
          ? api
              .get(`/favorite-words/check/${chapterId}/${normalizedSentenceId}/${encodeURIComponent(cleanWord)}`)
              .catch(() => ({
                data: { is_favorited: false },
              }))
          : api
              .get(`/favorite-words/by-chapter/${chapterId}`)
              .then((res) => {
                const hasMatch = Array.isArray(res.data)
                  ? res.data.some((item) => {
                      const itemSentenceRaw = item.sentence_id;
                      const itemSentenceId =
                        typeof itemSentenceRaw === 'number' && Number.isFinite(itemSentenceRaw) && itemSentenceRaw !== 0
                          ? itemSentenceRaw
                          : null;
                      return itemSentenceId === null && item.word === cleanWord;
                    })
                  : false;
                return { data: { is_favorited: hasMatch } };
              })
              .catch(() => ({
                data: { is_favorited: false },
              }));

      const [youdaoResponse, favoriteResponse] = await Promise.all([
        api.get(`/dict/youdao/lookup/${encodeURIComponent(cleanWord)}`).catch(err => ({ 
          data: { success: false, message: '查询失败' } 
        })),
        favoriteCheckPromise
      ]);
      
      console.log('Youdao查词结果:', youdaoResponse.data);
      console.log('收藏状态:', favoriteResponse.data);
      
      // 存储Youdao查询结果
      const youdaoResult = {
        success: youdaoResponse.data.success,
        data: youdaoResponse.data.data,
        message: youdaoResponse.data.message
      };

      setDictResults((prev) => ({
        ...prev,
        youdao: youdaoResult,
      }));
      
      // 设置主要查询结果（用于收藏功能，包含两个字典的定义）
      const hasYoudaoResult = youdaoResult.success;
      const youdaoDefinition = hasYoudaoResult ? youdaoResult.data.definition : null;
      const youdaoPhonetic = youdaoResult.data?.phonetics?.uk || youdaoResult.data?.phonetics?.us || null;

      if (hasYoudaoResult) {
        setWordLookup({
          word: cleanWord,
          success: true,
          phonetic: youdaoPhonetic,
          youdao_definition: youdaoDefinition,
          collins_definition: null,
        });
      } else {
        setWordLookup({
          word: cleanWord,
          success: false,
          message: '在字典中未找到该单词的释义'
        });
      }
      
      // 设置收藏状态
      setIsFavorited(favoriteResponse.data?.is_favorited || false);
      
    } catch (error) {
      console.error('查词失败:', error);
      const errorResult = {
        success: false,
        message: '网络错误，请稍后重试'
      };
      
      setDictResults({
        youdao: errorResult,
      });
      
      setWordLookup({
        word: cleanWord,
        success: false,
        message: '网络错误，请稍后重试'
      });
    } finally {
      setDictLoading((prev) => ({ ...prev, youdao: false }));
      setLookupLoading(false);
    }
  };

  // 处理字典标签切换
  const handleDictTabClick = (dictName) => {
    setActiveDict(dictName);
  };

  // 按需加载 Collins：仅在点击 Collins 标签后请求
  const fetchCollinsIfNeeded = useCallback(
    async (word) => {
      if (!word || dictLoading?.collins) return;
      setDictLoading((prev) => ({ ...prev, collins: true }));
      try {
        const collinsResponse = await api
          .get(`/dict/collins/lookup/${encodeURIComponent(word)}`)
          .catch(() => ({ data: { success: false, message: '查询失败' } }));
        const collinsResult = {
          success: collinsResponse.data.success,
          data: collinsResponse.data.data,
          message: collinsResponse.data.message,
        };
        setDictResults((prev) => ({ ...prev, collins: collinsResult }));
      } catch (err) {
        setDictResults((prev) => ({
          ...prev,
          collins: { success: false, message: '网络错误，请稍后重试' },
        }));
      } finally {
        setDictLoading((prev) => ({ ...prev, collins: false }));
      }
    },
    [dictLoading?.collins]
  );

  // 当切换到 Collins 标签且尚未加载时触发请求
  useEffect(() => {
    if (activeDict === 'collins' && wordLookup?.word) {
      const hasCollins = dictResults?.collins;
      if (!hasCollins && !dictLoading?.collins) {
        fetchCollinsIfNeeded(wordLookup.word);
      }
    }
  }, [activeDict, wordLookup?.word, dictResults?.collins, dictLoading?.collins, fetchCollinsIfNeeded]);


  const playPronunciation = (type) => {
    if (!wordLookup?.word) return;
    const encodedWord = encodeURIComponent(wordLookup.word);
    const audio = new Audio(`http://dict.youdao.com/dictvoice?type=${type}&audio=${encodedWord}`);
    audio.play().catch((err) => {
      console.error('播放发音失败:', err);
    });
  };

  // 处理定义中链接的点击事件
  const handleDefinitionClick = (e) => {
    // 检查点击的是否是 <a> 标签
    if (e.target.tagName === 'A') {
      e.preventDefault(); // 阻止默认链接行为
      
      // 获取链接文本作为新的查询词
      const linkText = e.target.textContent.trim();
      
      // 清理单词（移除标点符号）
      const cleanWord = linkText.replace(/[^\w\s]/gi, '').trim().toLowerCase();
      
      if (cleanWord && cleanWord.length >= 2) {
        console.log('从定义中点击查询单词:', cleanWord);
        
        // 使用当前句子ID（如果有的话）
        const sentenceId = currentSentenceId !== null ? currentSentenceId : null;
        
        // 调用单词查询函数
        handleWordClick(cleanWord, sentenceId);
      }
    }
  };

  const handleModalWordLookup = (rawWord) => {
    if (typeof rawWord !== 'string') return;
    const trimmed = rawWord.trim();
    if (!trimmed) return;
    const sentenceId =
      currentSentenceId !== null && currentSentenceId !== undefined
        ? currentSentenceId
        : null;
    handleWordClick(trimmed, sentenceId);
  };

  // 关闭单词弹窗
  const closeWordModal = () => {
    if (wordModalHistory.length > 0) {
      const previous = wordModalHistory[wordModalHistory.length - 1];
      setWordModalHistory((prev) => prev.slice(0, -1));
      setWordLookup(previous.wordLookup || null);
      setDictResults(previous.dictResults || {});
      setDictLoading(previous.dictLoading || {});
      setIsFavorited(!!previous.isFavorited);
      setFavoriteLoading(!!previous.favoriteLoading);
      setActiveDict(previous.activeDict || 'youdao');
      setCurrentSentenceId(previous.sentenceId ?? null);
      setLookupLoading(!!previous.lookupLoading);
      setShowWordModal(true);
    } else {
      setShowWordModal(false);
      setWordLookup(null);
      setCurrentSentenceId(null);
      setIsFavorited(false);
      setFavoriteLoading(false);
      setActiveDict('youdao');
      setDictResults({});
      setDictLoading({});
      setLookupLoading(false);
      setWordModalHistory([]);
    }
  };

  // 处理收藏单词
  const handleFavoriteWord = async () => {
    if (!wordLookup) {
      console.log('收藏条件不满足，退出');
      return;
    }

    const targetSentenceId =
      typeof currentSentenceId === 'number' && Number.isFinite(currentSentenceId)
        ? currentSentenceId
        : null;
    const normalizedWord = wordLookup.word?.trim().toLowerCase();

    setFavoriteLoading(true);
    
    try {
      if (isFavorited) {
        // 取消收藏 - 需要先获取收藏ID
        const response = await api.get(`/favorite-words/by-chapter/${chapterId}`);
        const favoriteItem = response.data.find(item => {
          const itemSentenceRaw = item.sentence_id;
          const itemSentenceId =
            typeof itemSentenceRaw === 'number' && Number.isFinite(itemSentenceRaw) && itemSentenceRaw !== 0
              ? itemSentenceRaw
              : null;
          const matchesWord = item.word === wordLookup.word;
          if (!matchesWord) return false;
          if (targetSentenceId === null) {
            return itemSentenceId === null;
          }
          return itemSentenceId === targetSentenceId;
        });
        
        if (favoriteItem) {
          await api.delete(`/favorite-words/${favoriteItem.id}`);
          setIsFavorited(false);
          console.log('取消收藏单词成功');

          setChapterWordSet((prev) => {
            if (!prev || typeof prev.has !== 'function') {
              return prev;
            }
            const next = new Set(prev);
            if (normalizedWord) {
              next.delete(normalizedWord);
            }
            return next;
          });

          setChapterWords((prev) => {
            if (!Array.isArray(prev)) {
              return prev;
            }
            if (!normalizedWord) {
              return prev;
            }
            return prev.filter((item) => (item?.word || '').toLowerCase() !== normalizedWord);
          });

          await fetchChapterWords();
        }
      } else {
        // 添加收藏
        const favoriteData = {
          chapter_id: parseInt(chapterId, 10),
          word: wordLookup.word,
        };

        if (targetSentenceId !== null) {
          favoriteData.sentence_id = targetSentenceId;
        }

        console.log('收藏单词:', favoriteData.word);

        const response = await api.post('/favorite-words/', favoriteData);
        
        if (response.status === 200 || response.status === 201) {
          setIsFavorited(true);
          console.log('收藏成功:', response.data);

          if (normalizedWord) {
            setChapterWordSet((prev) => {
              if (!prev || typeof prev.has !== 'function') {
                return new Set([normalizedWord]);
              }
              const next = new Set(prev);
              next.add(normalizedWord);
              return next;
            });

            setChapterWords((prev) => {
              const existing = Array.isArray(prev)
                ? prev.some((item) => (item?.word || '').toLowerCase() === normalizedWord)
                : false;
              if (existing) {
                return prev;
              }
              const payload = response.data || {};
              const entry = {
                id: payload.id,
                word: wordLookup.word,
                sentence_id: targetSentenceId,
                ...payload,
              };
              return Array.isArray(prev) ? [...prev, entry] : [entry];
            });
          }

          await fetchChapterWords();
        }
      }
    } catch (error) {
      console.error('收藏操作失败:', error);
      
      if (error.response?.status === 401) {
        console.log('请先登录');
        alert('请先登录后再操作');
      } else if (error.response?.status === 409) {
        // 已经收藏过了
        setIsFavorited(true);
        console.log('单词已经收藏过了');
      } else if (error.response?.status === 403) {
        // 403 由 api 拦截器处理（跳转 VipLimitPage），此处不弹窗
        console.log('每日限额已达上限');
      } else {
        console.log('操作失败，请稍后重试');
        alert('操作失败，请稍后重试');
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  // 获取句子收藏状态
  const fetchSentenceFavorites = async () => {
    try {
      const response = await api.get(withApiPrefix(`favorite-sentences/by-chapter/${chapterId}`));
      const favorites = {};
      response.data.forEach(item => {
        favorites[item.sentence_id] = true;
      });
      setSentenceFavorites(favorites);
    } catch (error) {
      console.error('获取句子收藏状态失败:', error);
      // 如果是认证错误，忽略（用户未登录）
      if (error.response?.status !== 401) {
        console.error('获取句子收藏状态时发生错误:', error);
      }
    }
  };

  const fetchAiNoteSaved = useCallback(async () => {
    if (!chapterId) {
      return;
    }

    try {
      const response = await api.get('/user-notes/', {
        params: {
          chapter_id: parseInt(chapterId, 10),
          limit: 200,
        },
      });

      const savedMap = {};
      const savedSet = new Set();
      const notes = Array.isArray(response.data) ? response.data : [];
      notes.forEach((note) => {
        if (!note) {
          return;
        }
        const noteContentValue = typeof note.note_content === 'string'
          ? note.note_content.trim()
          : String(note.note_content ?? '').trim();
        if (noteContentValue === '-1' && note.sentence_id != null) {
          const keyStr = String(note.sentence_id);
          const numericSentenceId = Number(note.sentence_id);
          savedMap[keyStr] = note.id;
          if (Number.isFinite(numericSentenceId)) {
            savedMap[numericSentenceId] = note.id;
            savedSet.add(numericSentenceId);
          }
        }
      });
      setAiNoteSaved(savedMap);
      setAiNoteSavedSet(savedSet);
      console.log('[AI Notes] fetched saved sentences', Array.from(savedSet));
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('获取用户笔记失败:', error);
      }
    }
  }, [chapterId]);

  const aiNotesLoadedRef = useRef(null);

  useEffect(() => {
    if (!bookId || !chapterId) return;

    setAiNoteSaved({});
    setAiNoteSavedSet(new Set());
    setAiNoteSaving({});
    aiNotesLoadedRef.current = null;
    setAudioNeeded(activeTab === 'listening');
    setEnAudioReady(false);

    let cancelled = false;
    (async () => {
      await fetchChapterContent();
      if (cancelled) return;

      fetchBookInfo();
      fetchAdjacentChapters();
      fetchSentenceFavorites();
      fetchChapterWords();
      setEnAudioReady(true);

      if (bookId) {
        api.post('/history-chapters/', {
          novel_id: Number(bookId),
          chapter_id: Number(chapterId),
        }).catch((err) => console.error('记录阅读历史失败:', err));
      }
    })();

    return () => { cancelled = true; };
  }, [bookId, chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'notes' && chapterId && aiNotesLoadedRef.current !== chapterId) {
      aiNotesLoadedRef.current = chapterId;
      fetchAiNoteSaved();
    }
  }, [activeTab, chapterId, fetchAiNoteSaved]);

  // 获取本章节收藏的单词列表
  const fetchChapterWords = async () => {
    try {
      setWordsLoading(true);
      const response = await api.get(`/favorite-words/by-chapter/${chapterId}`);
      const words = response.data;
      setChapterWords(words);
      setChapterWordSet(new Set(words.map(word => word.word?.toLowerCase())));
      setWordFavoriteLoading({});

      // 如果设置了显示所有单词句子，需要获取所有单词的句子详情
      if (showAllWordSentences && words.length > 0) {
        // 延迟执行，确保状态更新完成
        setTimeout(async () => {
          for (const wordItem of words) {
            const wordId = wordItem.id;
            if (!expandedSentences[wordId]) {
              await handleShowSentenceDetail(wordItem);
            }
          }
        }, 50);
      }
    } catch (error) {
      console.error('获取章节单词失败:', error);
      setChapterWordSet(new Set());
      // 如果是认证错误，忽略（用户未登录）
      if (error.response?.status !== 401) {
        console.error('获取章节单词时发生错误:', error);
      }
    } finally {
      setWordsLoading(false);
    }
  };

  // 处理句子收藏
  const handleSentenceFavorite = async (sentenceId, originalText, translatedText, timingInfo) => {
    const sentenceKey = sentenceId.toString();
    
    // 如果正在加载，忽略点击
    if (sentenceFavoriteLoading[sentenceKey]) {
      return;
    }

    // 设置加载状态
    setSentenceFavoriteLoading(prev => ({ ...prev, [sentenceKey]: true }));

    try {
      const isCurrentlyFavorited = sentenceFavorites[sentenceId];
      
      if (isCurrentlyFavorited) {
        // 取消收藏 - 需要先获取收藏ID
        const response = await api.get(withApiPrefix(`favorite-sentences/by-chapter/${chapterId}`));
        const favoriteItem = response.data.find(item => item.sentence_id === sentenceId);

        if (favoriteItem) {
          await api.delete(withApiPrefix(`favorite-sentences/${favoriteItem.id}`));
          setSentenceFavorites(prev => ({ ...prev, [sentenceId]: false }));
          console.log('取消收藏句子成功');
        }
      } else {
        // 添加收藏
        const favoriteData = {
          chapter_id: parseInt(chapterId),
          sentence_id: sentenceId,
          content_zh: originalText || null,
          content_en: translatedText || null,
          en_start_timestamp: timingInfo?.en?.start ?? null,
          en_end_timestamp: timingInfo?.en?.end ?? null,
          cn_start_timestamp: timingInfo?.cn?.start ?? null,
          cn_end_timestamp: timingInfo?.cn?.end ?? null,
        };

        const response = await api.post(withApiPrefix('favorite-sentences/'), favoriteData);
        if (response.data?.id) {
          sentenceFavoriteLoading[sentenceKey] = false;
        }
        setSentenceFavorites(prev => ({ ...prev, [sentenceId]: true }));
        console.log('收藏句子成功');
      }
    } catch (error) {
      console.error('句子收藏操作失败:', error);
      
      if (error.response?.status === 401) {
        alert('请先登录后再收藏句子');
      } else if (error.response?.status === 409) {
        // 已经收藏过了，刷新收藏状态
        try {
          const response = await api.get(withApiPrefix(`favorite-sentences/by-chapter/${chapterId}`));
          const favorites = {};
          response.data.forEach(item => {
            favorites[item.sentence_id] = true;
          });
          setSentenceFavorites(favorites);
        } catch (refreshError) {
          console.error('刷新收藏状态失败:', refreshError);
          setSentenceFavorites(prev => ({ ...prev, [sentenceId]: true }));
        }
      } else if (error.response?.status === 403) {
        // 403 由 api 拦截器处理（跳转 VipLimitPage），此处不弹窗
        console.log('每日限额已达上限');
      } else {
        alert('操作失败，请稍后重试');
      }
    } finally {
      // 清除加载状态
      setSentenceFavoriteLoading(prev => ({ ...prev, [sentenceKey]: false }));
    }
  };

  const handleToggleAiNote = async (sentenceId, notePayload = {}) => {
    const numericSentenceId = Number(sentenceId);
    if (!Number.isFinite(numericSentenceId) || numericSentenceId <= 0) {
      console.warn('[AI Notes] 无法收藏：句子 ID 非法', sentenceId);
      return;
    }

    const sentenceKey = numericSentenceId.toString();
    if (aiNoteSaving[sentenceKey]) {
      return;
    }

    setAiNoteSaving((prev) => ({ ...prev, [sentenceKey]: true }));

    const existingNoteId =
      notePayload.noteId ??
      aiNoteSaved[sentenceKey] ??
      aiNoteSaved[numericSentenceId] ??
      null;

    try {
      if (existingNoteId) {
        await api.delete(`/user-notes/${existingNoteId}`);
        setAiNoteSaved((prev) => {
          const next = { ...prev };
          delete next[sentenceKey];
          delete next[numericSentenceId];
          return next;
        });
        setAiNoteSavedSet((prev) => {
          const next = new Set(prev);
          next.delete(numericSentenceId);
          return next;
        });
      } else {
        const noteContent = '-1';

        const payload = {
          note_content: noteContent,
          sentence_id: numericSentenceId,
          chapter_id: parseInt(chapterId, 10),
          novel_id: book?.id ?? parseInt(bookId, 10),
          volume_id: chapter?.volume?.id ?? chapter?.volume_id ?? null,
        };

        const response = await api.post('/user-notes/', payload);
        const createdId = response.data?.id;
        if (createdId !== undefined && createdId !== null) {
          setAiNoteSaved((prev) => ({
            ...prev,
            [sentenceKey]: createdId,
            [numericSentenceId]: createdId,
          }));
          setAiNoteSavedSet((prev) => {
            const next = new Set(prev);
            next.add(numericSentenceId);
            return next;
          });
        } else {
          await fetchAiNoteSaved();
        }
      }
    } catch (error) {
      console.error('处理 AI 笔记收藏失败:', error);
      if (error.response?.status === 401) {
        alert('请先登录后再收藏 AI 笔记');
      } else if (error.response?.status === 403) {
        // 403 由 api 拦截器处理（跳转 VipLimitPage），此处不弹窗
        console.log('每日限额已达上限');
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('操作失败，请稍后重试');
      }
    } finally {
      setAiNoteSaving((prev) => ({ ...prev, [sentenceKey]: false }));
    }
  };

  const handleSentenceNote = (sentenceId, originalText, translatedText) => {
    setNoteModalState({
      visible: true,
      sentenceId,
      originalText: originalText?.trim() || '',
      translatedText: translatedText?.trim() || ''
    });
    setNoteContent('');
    setNoteError('');
  };

  const handleCloseNoteModal = () => {
    if (noteSubmitting) {
      return;
    }
    setNoteModalState({
      visible: false,
      sentenceId: null,
      originalText: '',
      translatedText: ''
    });
    setNoteContent('');
    setNoteError('');
  };

  const handleSubmitNote = async () => {
    if (!noteModalState.visible || noteModalState.sentenceId === null) {
      return;
    }

    const trimmedContent = noteContent.trim();
    if (!trimmedContent) {
      setNoteError('请输入笔记内容');
      return;
    }

    const sentenceKey = noteModalState.sentenceId.toString();
    setNoteError('');
    setNoteSubmitting(true);
    setSentenceNoteLoading(prev => ({
      ...prev,
      [sentenceKey]: true
    }));

    try {
      const payload = {
        note_content: trimmedContent,
        sentence_id: noteModalState.sentenceId,
        chapter_id: parseInt(chapterId, 10),
        novel_id: book?.id ?? parseInt(bookId, 10),
        volume_id: chapter?.volume?.id ?? chapter?.volume_id ?? null
      };

      await api.post('/user-notes/', payload);

      setNoteModalState({
        visible: false,
        sentenceId: null,
        originalText: '',
        translatedText: ''
      });
      setNoteContent('');
      alert('笔记已保存');
    } catch (error) {
      console.error('保存笔记失败:', error);
      if (error.response?.status === 401) {
        alert('请先登录后再保存笔记');
        setNoteModalState(prev => ({ ...prev, visible: false }));
      } else {
        const message = error.response?.data?.detail || '保存笔记失败，请稍后重试';
        setNoteError(message);
      }
    } finally {
      setNoteSubmitting(false);
      setSentenceNoteLoading(prev => ({
        ...prev,
        [sentenceKey]: false
      }));
    }
  };

  // 跳转到上一章（replace 避免历史栈膨胀）
  const handlePreviousChapter = () => {
    if (previousChapter) {
      navigate(`/read?bookid=${bookId}&chapterid=${previousChapter.id}`, { replace: true });
    }
  };

  // 跳转到下一章（replace 避免历史栈膨胀）
  const handleNextChapter = () => {
    if (nextChapter) {
      navigate(`/read?bookid=${bookId}&chapterid=${nextChapter.id}`, { replace: true });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'listening') {
      setAudioNeeded(true);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" text="加载章节内容中..." />
      </div>
    );
  }

  if (error || !chapter) {
    return (
      <div className={styles.error}>
        <h2>加载失败</h2>
        <p>{error || '章节不存在'}</p>
        <div className={styles.errorActions}>
          <button onClick={handleBackToBook} className={styles.backBtn} type="button" aria-label="返回书籍详情">
            <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
          </button>
          <button onClick={handleBackToHome} className={styles.homeBtn}>
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chapterReader}>
      {/* 顶部导航栏 */}
      <div className={styles.navbar}>
        <div className={styles.navLeft}>
          <button onClick={handleBackToBook} className={styles.backBtn} type="button" aria-label="返回书籍详情">
            <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
          </button>
        </div>

        <div
          className={styles.tabNavigation}
          style={{ 
            '--highlight-left': highlightStyle.left,
            '--highlight-width': highlightStyle.width
          }}
        >
          <span className={styles.tabHighlight} aria-hidden="true" />
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              ref={(el) => { 
                tabButtonRefs.current[tab.key] = el;
                // 如果是当前激活的 tab 且 DOM 已就绪，立即计算
                if (tab.key === activeTab && el && el.offsetWidth > 0) {
                  requestAnimationFrame(() => {
                    updateHighlight();
                  });
                }
              }}
              className={`${styles.tabButton} ${activeTab === tab.key ? styles.active : ''}`}
              onClick={() => handleTabChange(tab.key)}
              type="button"
              disabled={tab.disabled}
              title={tab.title}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.navRight}>
          <button
            type="button"
            className={styles.learnBtn}
            title="学习"
            onClick={() => {
              const target = `/chapterlearn?chapterid=${chapterId}&bookid=${bookId}&tab=words`;
              console.log('[ChapterReader] Learn button clicked, navigating to:', target);
              navigate(target);
              console.log('[ChapterReader] navigate() called, current URL:', window.location.href);
            }}
          >
            <LearnIcon size={20} />
          </button>
          <div className={styles.pdfBtnWrapper}>
            <button
              type="button"
              className={`${styles.navIconBtn} ${!isVip ? styles.navIconBtnLocked : ''}`}
              title={isVip ? '导出PDF' : '导出PDF（VIP）'}
              onClick={handleExportPDF}
              disabled={pdfExportLoading}
            >
              {pdfExportLoading ? (
                <LoaderIcon size={18} />
              ) : (
                <>
                  <span className="material-icons-round" style={{ fontSize: '20px' }}>picture_as_pdf</span>
                  {!isVip && <span className={styles.vipDot} />}
                </>
              )}
            </button>
            {showPdfVipTip && !isVip && (
              <div className={styles.pdfVipTip}>
                <div className={styles.pdfVipTipArrow} />
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'rgb(var(--theme-50))' }}>lock</span>
                <p className={styles.pdfVipTipText}>PDF 导出为 VIP 专属功能</p>
                <div className={styles.pdfVipTipActions}>
                  <button
                    type="button"
                    className={styles.pdfVipTipBtn}
                    onClick={() => { setShowPdfVipTip(false); navigate('/vip/renewal'); }}
                  >
                    去充值
                  </button>
                  <button
                    type="button"
                    className={styles.pdfVipTipClose}
                    onClick={() => setShowPdfVipTip(false)}
                  >
                    关闭
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* PDF 额度用尽 / 失败 提示窗（含续费） */}
          {showPdfQuotaModal && (
            <div className={styles.pdfQuotaModalOverlay} onClick={() => setShowPdfQuotaModal(false)}>
              <div className={styles.pdfQuotaModalCard} onClick={(e) => e.stopPropagation()}>
                <span className={`material-symbols-outlined ${styles.pdfQuotaModalIcon}`}>download_done</span>
                <h3 className={styles.pdfQuotaModalTitle}>提示</h3>
                <p className={styles.pdfQuotaModalMessage}>{pdfQuotaModalMessage}</p>
                <div className={styles.pdfQuotaModalActions}>
                  <button
                    type="button"
                    className={styles.pdfQuotaModalRenew}
                    onClick={() => { setShowPdfQuotaModal(false); navigate('/vip/renewal'); }}
                  >
                    <span className="material-icons-round" style={{ fontSize: 18 }}>stars</span>
                    续费
                  </button>
                  <button
                    type="button"
                    className={styles.pdfQuotaModalClose}
                    onClick={() => setShowPdfQuotaModal(false)}
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className={styles.settingsContainer} ref={settingsRef}>
            <button 
              className={styles.settingsBtn}
              onClick={() => setShowSettings(!showSettings)}
              title="阅读设置"
            >
              <SettingsIcon size={20} />
            </button>

            {showSettings && (
              <div className={styles.settingsPanel}>
                <div className={styles.settingsPanelHeader}>
                  <h3>阅读设置</h3>
                  <button
                    className={styles.closePanelBtn}
                    onClick={() => setShowSettings(false)}
                  >
                    <CloseIcon size={18} />
                  </button>
                </div>

                <div className={styles.settingsPanelContent}>
                  <div className={styles.settingGroup}>
                    <div className={styles.settingLabelRow}>
                      <span className={styles.settingLabelInline}>字号 & 行距</span>
                    </div>
                    <div className={styles.settingSliderGroup}>
                      <div className={styles.sliderBlock}>
                        <div className={styles.sliderHintsRow}>
                          <span className={styles.settingHint}>小</span>
                          <span className={styles.settingHint}>大</span>
                        </div>
                        <div className={styles.settingSliderRow}>
                          <input
                            className={styles.sliderInput}
                            type="range"
                            min="12"
                            max="32"
                            step="1"
                            value={fontSize}
                            onChange={(e) => setFontSize(Number(e.target.value))}
                          />
                          <span className={styles.settingValue}>{fontSize}px</span>
                        </div>
                      </div>
                      <div className={styles.sliderBlock}>
                        <div className={styles.sliderHintsRow}>
                          <span className={styles.settingHint}>紧凑</span>
                          <span className={styles.settingHint}>宽松</span>
                        </div>
                        <div className={styles.settingSliderRow}>
                          <input
                            className={styles.sliderInput}
                            type="range"
                            min="1.2"
                            max="2.5"
                            step="0.05"
                            value={lineHeight}
                            onChange={(e) => setLineHeight(Number(e.target.value))}
                          />
                          <span className={styles.settingValue}>{lineHeight.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.settingGroup}>
                    <label className={styles.settingLabel}>主题</label>
                    <select
                      className={styles.themeSelector}
                      onChange={(e) => {
                        const theme = colorThemes[parseInt(e.target.value)];
                        applyColorTheme(theme);
                      }}
                      value={colorThemes.findIndex(theme =>
                        theme.bg === backgroundColor && theme.chinese === chineseTextColor && theme.english === englishTextColor
                      )}
                    >
                      {colorThemes.map((theme, index) => (
                        <option key={index} value={index}>
                          {theme.name}
                        </option>
                      ))}
                    </select>
                  </div>

                
                  <div className={styles.settingGroup}>
                    <label className={styles.settingLabel}>阅读模式</label>
                    <div className={styles.displayModeSettings}>
                      {displayModeOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`${styles.displayModeOptionButton} ${displayMode === option.value ? styles.displayModeOptionButtonActive : ''}`}
                          onClick={() => handleDisplayModeChange(option.value)}
                          title={option.description}
                        >
                          <span className={styles.displayModeIcon}>{option.icon}</span>
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
        </div>

      {/* 章节内容 */}
      <div className={styles.chapterContent}>
        <div className={styles.chapterHeader}>
          <div 
            className={styles.chapterTitle}
            // style={{ color: chineseTextColor }}
          >
            {(() => {
              try {
                const contentData = JSON.parse(chapter.content);
                return contentData.chapter_title || chapter.title;
              } catch (e) {
                return chapter.title;
              }
            })()}
        </div>
          {/* <div className={styles.chapterMeta}>
            <span>第 {chapter.order_index} 章</span>
            {chapter.volume && (
              <>
                <span className={styles.separator}>·</span>
                <span>{chapter.volume.title}</span>
              </>
            )}
            {(() => {
              // 显示JSON中的卷信息（如果有的话）
              try {
                const contentData = JSON.parse(chapter.content);
                if (contentData.volume && contentData.volume !== chapter.volume?.title) {
                  return (
                    <>
                      <span className={styles.separator}>·</span>
                      <span>{contentData.volume}</span>
                    </>
                  );
                }
              } catch (e) {
                // 忽略JSON解析错误
              }
              return null;
            })()}
          </div> */}
        </div>



        {/* 标签页内容 */}
        <div className={styles.tabContent}>
          {activeTab === 'article' && (
            <div 
              className={styles.chapterText}
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: lineHeight
              }}
            >
              <SentencesSection
                chapter={chapter}
                displayMode={displayMode}
                chineseTextColor={chineseTextColor}
                englishTextColor={englishTextColor}
                fontSize={fontSize}
                lineHeight={lineHeight}
                sentenceFavorites={sentenceFavorites}
                sentenceFavoriteLoading={sentenceFavoriteLoading}
                sentenceNoteLoading={sentenceNoteLoading}
                highlightSentenceId={highlightSentenceId}
                backgroundColor={backgroundColor}
                chapterWordSet={chapterWordSet}
                sentenceTimings={sentenceTimings}
                onSentenceFavorite={handleSentenceFavorite}
                onSentenceNote={handleSentenceNote}
                onWordClick={handleWordClick}
                onSentencePronounce={handlePlaySentenceAudio}
              />
            </div>
          )}
          
          {activeTab === 'listening' && (
            <ChapterPlayer
              chapter={chapter}
              audioUrls={audioUrls}
              displayMode={displayMode}
              chineseTextColor={chineseTextColor}
              englishTextColor={englishTextColor}
              sentenceFavorites={sentenceFavorites}
              sentenceFavoriteLoading={sentenceFavoriteLoading}
              sentenceNoteLoading={sentenceNoteLoading}
              highlightSentenceId={highlightSentenceId}
              backgroundColor={backgroundColor}
              chapterWordSet={chapterWordSet}
              fontSize={fontSize}
              lineHeight={lineHeight}
              onSentenceFavorite={handleSentenceFavorite}
              onSentenceNote={handleSentenceNote}
              onWordClick={handleWordClick}
              controlsContainer={listeningControlsContainer}
            />
          )}

          {activeTab === 'notes' && (
            <ChapterAINotes
              chapter={chapter}
              onWordClick={handleWordClick}
              onSaveNote={handleToggleAiNote}
              savedNotes={aiNoteSaved}
              savedSentenceSet={aiNoteSavedSet}
              savingNotes={aiNoteSaving}
              sentenceTimings={sentenceTimings}
              onSentencePronounce={handlePlaySentenceAudio}
            />
          )}
 
        </div>
   <div className={styles.chapterActions}>
          <button
            type="button"
            className={`${styles.completionBtn} ${completionStatus.completed ? styles.completionBtnActive : ''}`}
            onClick={handleToggleCompletion}
            disabled={completionLoading}
          >
            {completionLoading
              ? '刷新中...'
              : completionStatus.completed
                ? '已完成本章阅读（点击取消）'
                : '完成本章阅读'}
          </button>
          {completionStatus.completed && completionStatus.completedAt && (
            <span className={styles.completionMeta}>
              已于 {completionDateFormatter.format(completionStatus.completedAt)} 完成
            </span>
          )}
          {completionError && (
            <span className={styles.completionError}>{completionError}</span>
          )}
        </div>
        {/* 章节导航 */}
        {showChapterNavigation && (
          <div className={`${styles.chapterNavigation} ${activeTab === 'listening' ? styles.chapterNavigationWithAudio : ''}`}>
            <button 
              className={styles.navBtn} 
              disabled={!previousChapter}
              onClick={handlePreviousChapter}
            >
              <span className={styles.navBtnIcon} aria-hidden="true">‹</span>
              <span>上一章</span>
            </button>
            <button onClick={handleBackToBook} className={styles.catalogBtn}>
              <span className="material-symbols-outlined text-2xl">format_list_bulleted</span>
            </button>
            <button 
              className={styles.navBtn} 
              disabled={!nextChapter}
              onClick={handleNextChapter}
            >
              <span>下一章</span>
              <span className={styles.navBtnIcon} aria-hidden="true">›</span>
            </button>
          </div>
        )}
      </div>

      {activeTab === 'listening' && (
        <div className={styles.bottomControls}>
            <div ref={listeningControlsRef} className={styles.bottomControlsSlot} />
          </div>
      )}


      <NoteModal
        visible={noteModalState.visible}
        sentenceIndex={noteModalState.sentenceId}
        originalText={noteModalState.originalText}
        translatedText={noteModalState.translatedText}
        value={noteContent}
        loading={noteSubmitting}
        error={noteError}
        onChange={setNoteContent}
        onClose={handleCloseNoteModal}
        onSubmit={handleSubmitNote}
        novelId={book?.id ?? parseInt(bookId, 10)}
        chapterId={parseInt(chapterId, 10)}
        volumeId={chapter?.volume?.id ?? chapter?.volume_id ?? null}
        chapterAiNotes={chapter?.ai_notes ?? chapter?.aiNotes ?? null}
        onWordClick={handleWordClick}
        onSaveAiNote={handleToggleAiNote}
        aiNoteSaved={aiNoteSaved}
        aiNoteSavedSet={aiNoteSavedSet}
        aiNoteSaving={aiNoteSaving}
      />

      <WordModal
        isOpen={showWordModal}
        wordLookup={wordLookup}
        activeDict={activeDict}
        dictLoading={dictLoading}
        dictResults={dictResults}
        favoriteLoading={favoriteLoading}
        isFavorited={isFavorited}
        onPlayPronunciation={playPronunciation}
        onFavoriteWord={handleFavoriteWord}
        onDictTabClick={handleDictTabClick}
        onDefinitionClick={handleDefinitionClick}
        onWordLookup={handleModalWordLookup}
        onClose={closeWordModal}
      />
    </div>
  );
};

export default ChapterReader;
